import {
  convertBoundingBoxDtoToMultiPolygon,
  BusinessArea,
  getEntries,
  getKeys,
  type LifestyleCategory,
  PropertyKind,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type FallbackSearch,
} from '@ev-tech/search-modules-api';

import { getListingSortOptionById, isNonEmptyObject } from './searchStateUtils';
import { resolveMapModeForSearchState } from './mapModeUtils';
import { type SmartSortConfig } from '../types/searchModuleTypes';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';
import { DEFAULT_PREFERENCES } from '../hooks/stores/search/slices/preferencesSlice';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, DEFAULT_SORTING_OPTION_ID } from '../hooks/stores/search/slices/optionsSlice';
import {
  DEFAULT_BUSINESS_AREA,
  DEFAULT_PROPERTY_MARKETING_TYPE,
  getSelectedPropertyTypeState,
  getSelectedPropertyTypeSubType,
} from '../hooks/stores/search/slices/objectTypeFilterSlice';
import { type MapMode } from '../hooks/stores/search/slices/mapInteractionSlice';
import { defaultLifestyleCategoryState } from '../hooks/stores/search/slices/lifestyleCategorySlice';
import { type SearchState } from '../hooks/stores/search/SearchStoreProvider';
import { SortingOptionId } from '../enums/enums';
import { SORT_BY_OPTIONS } from '../consts/consts';

type SearchListingsRequestFiltersParams = {
  filters: SearchListingsFilters;
  sortingOptionId?: SortingOptionId;
  smartSortPreferences?: SmartSortConfig;
};

type SearchFallbackSearch = FallbackSearch & {
  radiusAroundShops?: boolean;
  disableShopFilter?: boolean;
};

const isLipaSearchFilters = (filters: Pick<SearchListingsFilters, 'shopIds' | 'masterDataShopIds' | 'masterDataLiPaIds'>): boolean =>
  Boolean(filters.shopIds?.length || filters.masterDataShopIds?.length || filters.masterDataLiPaIds?.length);

export const LIPA_FALLBACK_SEARCH = {
  propertyCount: 23,
  activateUnderEquals: 23,
  newRadius: 50,
  radiusAroundShops: true,
  disableShopFilter: true,
} as const satisfies SearchFallbackSearch;

export const getFallbackSearch = (
  filters: Pick<SearchListingsFilters, 'searchRadius' | 'shopIds' | 'masterDataShopIds' | 'masterDataLiPaIds'>,
): SearchFallbackSearch => {
  if (isLipaSearchFilters(filters)) {
    return LIPA_FALLBACK_SEARCH;
  }

  return {
    activateUnderEquals: 10,
    propertyCount: 21,
    newRadius: 50 + (filters.searchRadius ?? 0),
  };
};

export const prepareSearchListingsFiltersForRequest = ({
  filters,
  sortingOptionId,
  smartSortPreferences,
}: SearchListingsRequestFiltersParams): SearchListingsFilters => {
  const preferences = sortingOptionId === SortingOptionId.SMART_SORT ? smartSortPreferences : filters.preferences;
  const sortingOption = getListingSortOptionById(sortingOptionId);

  return {
    ...filters,
    ...(sortingOption && { sortingOptions: [sortingOption] }),
    ...(isNonEmptyObject(preferences) && { preferences }),
    propertyKind: filters.propertyTypeSubType?.group ? PropertyKind.group : PropertyKind.object,
    fallbackSearch: getFallbackSearch(filters),
  };
};

export const mapSearchStateToSearchListingsFilters = (state: SearchState): SearchListingsFilters => {
  const businessArea = state.objectTypeCategory === 'development' ? BusinessArea.residential : state.objectTypeCategory;
  const lifestyleCategory = getEntries(state.lifestyleCategories)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const sortingOption = SORT_BY_OPTIONS.find((option) => option.id === state.sortingOptionId)?.sortingOption;

  const filters = {
    businessArea: [businessArea],
    propertyMarketingType: [state.marketingType],
    propertyTypeSubType: getSelectedPropertyTypeSubType(state.propertyTypeState, state.objectTypeCategory),
    // Features
    hasAirConditioning: state.features.hasAirConditioning ? true : undefined,
    hasBalcony: state.features.hasBalcony ? true : undefined,
    hasBasement: state.features.hasBasement ? true : undefined,
    hasBuiltInKitchen: state.features.hasBuiltInKitchen ? true : undefined,
    hasElevator: state.features.hasElevator ? true : undefined,
    hasGarage: state.features.hasGarage ? true : undefined,
    hasGarden: state.features.hasGarden ? true : undefined,
    hasDoorman: state.features.hasDoorman ? true : undefined,
    hasPatio: state.features.hasPatio ? true : undefined,
    hasSeaOrLakeView: state.features.hasSeaOrLakeView ? true : undefined,
    hasSecuritySystem: state.features.hasSecuritySystem ? true : undefined,
    hasSwimmingPool: state.features.hasSwimmingPool ? true : undefined,
    hasTennisCourt: state.features.hasTennisCourt ? true : undefined,
    hasTerrace: state.features.hasTerrace ? true : undefined,
    hasWaterfront: state.features.hasWaterfront ? true : undefined,
    hasHelipad: state.features.hasHelipad ? true : undefined,
    hasPriceUpdated: state.features.hasPriceUpdated ? true : undefined,
    hasFloorplan: state.multimedia.hasFloorplan ? true : undefined,
    hasVtourNormalized: state.multimedia.hasVtourNormalized ? true : undefined,
    hasVideoNormalized: state.multimedia.hasVideoNormalized ? true : undefined,
    // Range filters
    price: state.price,
    plotSurface: state.plotSurface,
    totalSurface: state.totalSurface,
    rooms: state.rooms,
    bathrooms: state.bathrooms,
    bedrooms: state.bedrooms,
    constructionYear: state.constructionYear,
    // shop filters
    shopIds: state.shopIds,
    masterDataShopIds: state.masterDataShopIds,
    masterDataLiPaIds: state.masterDataLiPaIds,
    // location filters
    listingIds: state.listingIds,
    searchRadius: state.searchRadius,
    multiPolygon: state.multiPolygon,
    placeIds: state.placeIds,
    // options
    sortingOptions: sortingOption ? [sortingOption] : undefined,
    // private office filters
    agentId: state.agentId,
    promotedBy: state.promotedBy,
    privateOffice: state.privateOffice,
    // lifestyle filters
    lifestyleCategory: lifestyleCategory.length > 0 ? lifestyleCategory : undefined,
    ...(isNonEmptyObject(state.preferences) && { preferences: state.preferences }),
  } satisfies SearchListingsFilters;

  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined)) as SearchListingsFilters;
};

export const mapSearchListingsFiltersToSearchState = ({
  filters,
  options,
  placeName,
  mapMode,
  sortingOptionId: sortingOptionIdOverride,
}: {
  filters: SearchListingsFilters;
  options?: Pick<SearchListingsOptions, 'page' | 'pageSize'>;
  placeName?: string;
  mapMode?: MapMode;
  sortingOptionId?: SortingOptionId;
}): SearchState => {
  const multiPolygon = filters.multiPolygon ?? (filters.boundingBox ? convertBoundingBoxDtoToMultiPolygon(filters.boundingBox) : undefined);
  const sortingOptionId =
    sortingOptionIdOverride ??
    SORT_BY_OPTIONS.find((option) => option.sortingOption === filters.sortingOptions?.[0])?.id ??
    DEFAULT_SORTING_OPTION_ID;
  return {
    objectTypeCategory: filters.propertyTypeSubType?.group ? 'development' : (filters.businessArea[0] ?? DEFAULT_BUSINESS_AREA),
    marketingType: filters.propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE,
    propertyTypeState: getSelectedPropertyTypeState(filters.propertyTypeSubType, filters.propertyType, filters.propertySubType),
    features: {
      hasAirConditioning: filters.hasAirConditioning ?? false,
      hasBalcony: filters.hasBalcony ?? false,
      hasBasement: filters.hasBasement ?? false,
      hasBuiltInKitchen: filters.hasBuiltInKitchen ?? false,
      hasElevator: filters.hasElevator ?? false,
      hasGarage: filters.hasGarage ?? false,
      hasGarden: filters.hasGarden ?? false,
      hasDoorman: filters.hasDoorman ?? false,
      hasPatio: filters.hasPatio ?? false,
      hasSeaOrLakeView: filters.hasSeaOrLakeView ?? false,
      hasSecuritySystem: filters.hasSecuritySystem ?? false,
      hasSwimmingPool: filters.hasSwimmingPool ?? false,
      hasTennisCourt: filters.hasTennisCourt ?? false,
      hasTerrace: filters.hasTerrace ?? false,
      hasWaterfront: filters.hasWaterfront ?? false,
      hasHelipad: filters.hasHelipad ?? false,
      hasPriceUpdated: filters.hasPriceUpdated ?? false,
    },
    multimedia: {
      hasFloorplan: filters.hasFloorplan ?? false,
      hasVtourNormalized: filters.hasVtourNormalized ?? false,
      hasVideoNormalized: filters.hasVideoNormalized ?? false,
    },
    // string filters
    preferences: filters.preferences ?? DEFAULT_PREFERENCES,
    // Range filters
    price: filters.price,
    plotSurface: filters.plotSurface,
    totalSurface: filters.totalSurface,
    rooms: filters.rooms,
    bathrooms: filters.bathrooms,
    bedrooms: filters.bedrooms,
    constructionYear: filters.constructionYear,
    // shop filters
    shopIds: filters.shopIds,
    masterDataShopIds: filters.masterDataShopIds,
    masterDataLiPaIds: filters.masterDataLiPaIds,
    // options
    page: options?.page ?? DEFAULT_PAGE,
    pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
    sortingOptionId,
    // private filters
    agentId: filters.agentId,
    promotedBy: filters.promotedBy,
    privateOffice: filters.privateOffice ?? false,
    // lifestyle filters
    lifestyleCategories: getKeys(defaultLifestyleCategoryState.lifestyleCategories).reduce(
      (acc, category) => {
        acc[category] = filters.lifestyleCategory?.includes(category) ?? false;
        return acc;
      },
      {} as Record<LifestyleCategory, boolean>,
    ),
    // location filters
    listingIds: filters.listingIds,
    searchRadius: filters.searchRadius ?? 0,
    multiPolygon,
    placeIds: filters.placeIds,
    ...(placeName && { placeName }),
    // search mode state
    searchMode: SearchMode.CLASSIC,
    modalSearchMode: SearchMode.CLASSIC,
    mapMode: resolveMapModeForSearchState(mapMode, multiPolygon),
  };
};
